import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import type { SxProps, Theme } from '@mui/material/styles';

import T from 'shared/atoms/Typography';
import type { BackgroundThemes } from 'shared/const';
import theme from 'shared/themes/default';

interface WrapperProps {
  static?: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${(props) =>
    !props.static &&
    css`
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translateY(-50%) translateX(-50%);
    `}
`;

const SpinnerValueWrapper = styled.div`
  position: relative;
`;

const ProgressWrapper = styled.div`
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface CircularSpinnerProps {
  bottomText?: string;
  color?: string;
  duration?: number;
  onTimeOut?: () => void;
  showProgress?: boolean;
  size?: number;
  static?: boolean;
  theme?: BackgroundThemes;
  thickness?: number;
  timer?: boolean;
}

const CircularSpinner = ({
  bottomText,
  color = theme.palette.brand,
  duration = 10,
  onTimeOut,
  showProgress,
  size = 75,
  static: staticProp = false,
  theme: themeLayout = 'dark',
  thickness = 4,
  timer,
}: CircularSpinnerProps) => {
  const [progress, setProgress] = useState(timer ? 100 : 0);
  const [seconds, setSeconds] = useState(1);

  useEffect(() => {
    setSeconds(1);

    if (showProgress || timer) {
      const timerInterval = setInterval(
        () =>
          setSeconds((seconds) =>
            seconds <= duration ? seconds + 1 : seconds
          ),
        1000
      );
      return () => clearInterval(timerInterval);
    }
    return undefined;
  }, [duration, showProgress, timer]);

  useEffect(() => {
    if (timer) {
      if (seconds > duration) {
        onTimeOut?.();
      } else {
        setProgress(100 - (seconds / duration) * 100);
      }
    } else {
      setProgress(
        Math.round((1 - Math.exp(-0.5 * (seconds * (10 / duration)))) * 100)
      );
    }
  }, [seconds, timer, duration, onTimeOut]);

  const sxTimer: SxProps<Theme> = {
    transform: 'rotate(-90deg) scaleY(-1) !important',
    '& .MuiCircularProgress-circleDeterminate': {
      transition: 'stroke-dashoffset 1s linear 0s',
    },
  };

  return (
    <Wrapper static={staticProp}>
      {!showProgress && !timer ? (
        <CircularProgress thickness={thickness} size={size} sx={{ color }} />
      ) : (
        <SpinnerValueWrapper>
          <CircularProgress
            thickness={thickness}
            size={size}
            sx={{
              color:
                themeLayout === 'dark'
                  ? theme.palette.darkDistinct
                  : theme.palette.lightDistinct,
            }}
            variant="determinate"
            value={100}
          />
          <CircularProgress
            thickness={thickness}
            size={size}
            sx={{
              color,
              position: 'absolute',
              strokeLinecap: 'round',
              left: 0,
              ...(timer ? sxTimer : {}),
            }}
            variant="determinate"
            value={progress}
          />
          <ProgressWrapper>
            <T
              color={
                themeLayout === 'dark'
                  ? theme.palette.lightPure
                  : theme.palette.mainDark
              }
              variant="headingXs"
              fontWeight="semibold"
            >
              {timer ? duration - seconds + 1 : `${progress}%`}
            </T>
          </ProgressWrapper>
        </SpinnerValueWrapper>
      )}
      {bottomText && (
        <T
          color={
            themeLayout === 'dark'
              ? theme.palette.disabledLight
              : theme.palette.disabledDark
          }
        >
          {bottomText}
        </T>
      )}
    </Wrapper>
  );
};

export default CircularSpinner;
export type { CircularSpinnerProps };
